/**
 * Predictive maintenance (preview).
 *
 * One question per row: how likely is it that this connector starts a NEW incident in the next
 * 30 days? The list is a ranking for attention, sorted by the tier the operator picks. Nothing on
 * this page acts on a charger; it only says where to look first.
 *
 * Why four tiers rather than one score: "fault", "won't start charging", "went silent" and "the
 * site's supply voltage is out of band" need different people. The last one is a grid problem,
 * so it is kept apart from the charger's own health.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { useNotice } from "../shared/useNotice";
import {
  ConnectorRiskDetail, ConnectorRiskRow, PredictiveSummary, Tier, TIERS, predictionsApi,
} from "../../../api/maintenance/predictions";
import "./predictions.scss";

const TIER_LABEL: Record<Tier, string> = {
  device: "Device", start: "Charging start", offline: "Offline", site: "Site supply",
};
const TIER_HINT: Record<Tier, string> = {
  device: "a fault, a charging-start failure or going silent",
  start: "connected but never charged, or a 0-unit session",
  offline: "a whole day without a heartbeat",
  site: "supply voltage out of band at the site (a grid condition, not a charger defect)",
};
const KIND_LABEL: Record<string, string> = {
  faulted_episode: "Fault", start_failure: "Charging didn't start", start_failure_faulted: "Start failed with a fault",
  zero_energy_fault: "0-unit session", silent_episode: "Went silent", dead: "Never came back",
  start_failure_transient: "Start failed, retry worked",
};

const pct = (p: number | null | undefined): string => (p == null ? "—" : `${Math.round(p * 100)}%`);

type Tone = "high" | "elevated" | "watch" | "low";
const tone = (p: number | null | undefined): Tone => {
  if (p == null) return "low";
  if (p >= 0.75) return "high";
  if (p >= 0.5) return "elevated";
  if (p >= 0.25) return "watch";
  return "low";
};

const daysAgo = (s: string | null | undefined): string => {
  if (!s) return "—";
  const d = Math.floor((Date.now() - new Date(s).getTime()) / 86_400_000);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  return `${d} days ago`;
};

const fmtDate = (s: string | null | undefined): string => (s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—");

const Risk: React.FC<{ p: number | null | undefined; strong?: boolean }> = ({ p, strong }) => (
  <span className={`pm-risk ${tone(p)} ${strong ? "strong" : ""}`}>{pct(p)}</span>
);

/* ------------------------------------------------------------------ drawer */

const Drawer: React.FC<{ id: number; tier: Tier; onClose: () => void; onTier: (t: Tier) => void }> = ({ id, tier, onClose, onTier }) => {
  const [d, setD] = useState<ConnectorRiskDetail | null>(null);
  const [err, setErr] = useNotice("error", "");
  useEffect(() => {
    setD(null);
    predictionsApi.detail(id).then((r) => setD(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load this connector"));
  }, [id, setErr]);

  const cur = d?.tiers.find((t) => t.tier === tier) || d?.tiers[0];
  const curve = (cur?.curve || []).map((pt) => ({ h: pt.h, p: Math.round(pt.p * 1000) / 10 }));
  const hist = (d?.history || []).filter((h) => h.tier === (cur?.tier || tier)).map((h) => ({ d: fmtDate(h.as_of), p: h.p30 == null ? null : Math.round(h.p30 * 1000) / 10 }));

  return (
    <div className="pm-drawer-bg" onClick={onClose}>
      <aside className="pm-drawer" onClick={(e) => e.stopPropagation()}>
        <header>
          <div>
            <h2>{d ? `${d.connector.device_name || d.connector.device_id} · connector ${d.connector.connector_no ?? "?"}` : "Loading…"}</h2>
            {d && <p className="sub">{[d.connector.station_name, d.connector.model, d.connector.firmware && `fw ${d.connector.firmware}`].filter(Boolean).join(" · ")}</p>}
          </div>
          <button className="pm-x" onClick={onClose} aria-label="Close">×</button>
        </header>
        {err && <div className="pm-note error">{err}</div>}
        {d && (
          <>
            <div className="pm-status">
              {d.in_failure_now ? <span className="pm-pill bad">In an incident now</span> : <span className="pm-pill muted">No open incident</span>}
              {d.last_incident_at && <span className="sub">last: {KIND_LABEL[d.last_incident_kind || ""] || d.last_incident_kind} · {daysAgo(d.last_incident_at)}</span>}
            </div>

            <table className="pm-table compact">
              <thead><tr><th>Tier</th><th className="num">7 d</th><th className="num">30 d</th><th className="num">90 d</th><th className="num">180 d</th><th className="num">1 y</th><th className="num">Rank</th></tr></thead>
              <tbody>
                {d.tiers.map((t) => (
                  <tr key={t.tier} className={t.tier === cur?.tier ? "selected clickable" : "clickable"} onClick={() => onTier(t.tier)}>
                    <td>{TIER_LABEL[t.tier]}</td>
                    <td className="num"><Risk p={t.p7} /></td>
                    <td className="num"><Risk p={t.p30} strong /></td>
                    <td className="num"><Risk p={t.p90} /></td>
                    <td className="num"><Risk p={t.p180} /></td>
                    <td className="num"><Risk p={t.p365} /></td>
                    <td className="num sub">{t.rank_pct == null ? "—" : `top ${Math.max(1, Math.round(100 - t.rank_pct + 1))}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {cur && (
              <section className="pm-chart">
                <h3>{TIER_LABEL[cur.tier]}: chance of a new incident within…</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={curve} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                    <CartesianGrid vertical={false} stroke="#e5e7eb" />
                    <XAxis dataKey="h" ticks={[7, 30, 90, 180, 365]} tickFormatter={(v: number) => (v >= 365 ? "1 y" : `${v} d`)} tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} type="number" domain={[1, 365]} />
                    <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(v: number) => `${v}%`} tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                    <Tooltip formatter={(v: any) => [`${v ?? 0}%`, "chance"]} labelFormatter={(h: any) => `within ${h} day${Number(h) === 1 ? "" : "s"}`} contentStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="p" stroke="#1d4ed8" strokeWidth={2} fill="#1d4ed8" fillOpacity={0.08} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </section>
            )}

            {hist.length > 1 && (
              <section className="pm-chart">
                <h3>30-day risk, day by day</h3>
                <ResponsiveContainer width="100%" height={120}>
                  <LineChart data={hist} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
                    <CartesianGrid vertical={false} stroke="#e5e7eb" />
                    <XAxis dataKey="d" tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
                    <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tickFormatter={(v: number) => `${v}%`} tick={{ fill: "#6b7280", fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                    <Tooltip formatter={(v: any) => [`${v ?? 0}%`, "30-day risk"]} contentStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="p" stroke="#1d4ed8" strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </section>
            )}

            <section>
              <h3>Recent incidents</h3>
              {d.events.length === 0 ? <p className="sub">None recorded for this connector.</p> : (
                <ul className="pm-events">
                  {d.events.map((e, i) => (
                    <li key={i}>
                      <span className={`pm-pill ${e.severity === "critical" ? "bad" : e.severity === "warning" ? "warn" : "muted"}`}>{e.severity || "info"}</span>
                      <span>{KIND_LABEL[e.kind] || e.kind}{e.error_code ? ` · ${e.error_code}` : ""}</span>
                      <span className="sub">{fmtDate(e.started_at)}{e.ended_at ? ` → ${fmtDate(e.ended_at)}` : " → open"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <p className="sub pm-foot">Model {d.model_version} · scored for {fmtDate(d.as_of)}</p>
          </>
        )}
      </aside>
    </div>
  );
};

/* -------------------------------------------------------------------- page */

const PredictionsConsole: React.FC = () => {
  const [summary, setSummary] = useState<PredictiveSummary | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [tier, setTier] = useState<Tier>("device");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [onlyFailing, setOnlyFailing] = useState(false);
  const [minRisk, setMinRisk] = useState(0);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<ConnectorRiskRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useNotice("error", "");
  const [selected, setSelected] = useState<number | null>(null);
  const pageSize = 50;

  useEffect(() => {
    predictionsApi.summary().then((r) => setSummary(r.data)).catch((e) => {
      if (e?.response?.status === 404) setDisabled(true);
      else setErr(e?.response?.data?.detail || "Could not load the summary");
    });
  }, [setErr]);

  const load = useCallback(() => {
    if (disabled) return;
    setLoading(true);
    predictionsApi.list({ tier, q: search || undefined, min_p30: minRisk || undefined, in_failure_now: onlyFailing || undefined, page, page_size: pageSize })
      .then((r) => { setRows(r.data.items); setTotal(r.data.total); })
      .catch((e) => { if (e?.response?.status === 404) setDisabled(true); else setErr(e?.response?.data?.detail || "Could not load predictions"); })
      .finally(() => setLoading(false));
  }, [tier, search, minRisk, onlyFailing, page, disabled, setErr]);

  useEffect(() => { load(); }, [load]);

  const tierSummary = summary?.tiers.find((t) => t.tier === tier);
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const p30Of = (r: ConnectorRiskRow, t: Tier): number | null =>
    t === "device" ? r.device_p30 : t === "start" ? r.start_p30 : t === "offline" ? r.offline_p30 : r.site_p30;

  return (
    <MaintenanceLayout title="Predictive maintenance">
      <div className="pm">
        <div className="pm-head">
          <span className="pm-pill info">PREVIEW</span>
          <span className="pm-note">Ranks connectors by the chance of a new incident. Read it as where to look first, not as an alarm.</span>
          {summary?.as_of && <span className="pm-note sub">scored for {fmtDate(summary.as_of)}</span>}
        </div>

        {disabled ? (
          <div className="pm-empty"><h3>Preview is switched off</h3><p>The predictive_maintenance_preview flag is disabled.</p></div>
        ) : (
          <>
            <div className="pm-tabs" role="tablist">
              {TIERS.map((t) => {
                const s = summary?.tiers.find((x) => x.tier === t);
                return (
                  <button key={t} role="tab" className={`pm-tab ${t === tier ? "active" : ""}`} onClick={() => { setTier(t); setPage(1); }} title={TIER_HINT[t]}>
                    <span>{TIER_LABEL[t]}</span>
                    {s && <span className="pm-tab-sub">{s.high} high · {s.elevated} elevated</span>}
                  </button>
                );
              })}
            </div>
            <p className="pm-hint sub">{TIER_LABEL[tier]} risk = {TIER_HINT[tier]}.{tierSummary ? ` ${tierSummary.connectors} connectors scored, ${tierSummary.in_failure_now} in an incident now.` : ""}</p>

            <form className="pm-toolbar" onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(q.trim()); }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Charger or station" aria-label="Search" />
              <select value={minRisk} onChange={(e) => { setMinRisk(Number(e.target.value)); setPage(1); }} aria-label="Minimum 30-day risk">
                <option value={0}>Any risk</option>
                <option value={0.25}>≥ 25% in 30 d</option>
                <option value={0.5}>≥ 50% in 30 d</option>
                <option value={0.75}>≥ 75% in 30 d</option>
              </select>
              <label className="pm-check"><input type="checkbox" checked={onlyFailing} onChange={(e) => { setOnlyFailing(e.target.checked); setPage(1); }} /> in an incident now</label>
              <span className="pm-spacer" />
              <button type="submit" className="pm-btn">Search</button>
              <button type="button" className="pm-btn ghost" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
            </form>

            {err && <div className="pm-note error">{err}</div>}

            <div className="pm-tablewrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Charger</th>
                    <th>Station</th>
                    {TIERS.map((t) => <th key={t} className={`num ${t === tier ? "sorted" : ""}`}>{TIER_LABEL[t]} 30 d</th>)}
                    <th className="num">7 d</th>
                    <th className="num">90 d</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && !loading && (
                    <tr><td colSpan={9} className="pm-empty-cell">{search || minRisk || onlyFailing ? "No connectors match these filters." : "No scores yet — the daily run has not written anything."}</td></tr>
                  )}
                  {rows.map((r, i) => (
                    <tr key={r.device_connector_id} className="clickable" onClick={() => setSelected(r.device_connector_id)}>
                      <td className="sub">{(page - 1) * pageSize + i + 1}</td>
                      <td>
                        <div>{r.display_name || r.device_name || r.device_id}</div>
                        <div className="sub">connector {r.connector_no ?? "?"}{r.model ? ` · ${r.model}` : ""}</div>
                      </td>
                      <td>{r.station_name || "—"}{r.org_name && <div className="sub">{r.org_name}</div>}</td>
                      {TIERS.map((t) => <td key={t} className={`num ${t === tier ? "sorted" : ""}`}><Risk p={p30Of(r, t)} strong={t === tier} /></td>)}
                      <td className="num"><Risk p={tier === "device" ? r.device_p7 : null} /></td>
                      <td className="num"><Risk p={tier === "device" ? r.device_p90 : null} /></td>
                      <td>
                        {r.in_failure_now ? <span className="pm-pill bad">in incident</span> : <span className="pm-pill muted">ok now</span>}
                        {r.last_incident_at && <div className="sub">{KIND_LABEL[r.last_incident_kind || ""] || r.last_incident_kind} · {daysAgo(r.last_incident_at)}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pm-pagination">
              <button className="pm-btn ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
              <span className="sub">{total === 0 ? "0" : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}</span>
              <button className="pm-btn ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
            </div>
            <p className="sub pm-foot">7 d and 90 d columns show the Device tier. Open a row for every tier and horizon.{summary?.model_version ? ` Model ${summary.model_version}.` : ""}</p>
          </>
        )}

        {selected !== null && <Drawer id={selected} tier={tier} onClose={() => setSelected(null)} onTier={setTier} />}
      </div>
    </MaintenanceLayout>
  );
};

export default PredictionsConsole;
